import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import locales from '../../../content.json';
import s from './index.module.scss';

import { calculatePrice } from '../../../utils/scripts/calculatePrice';

import Option from '../Option';
import PromoCode from '../../../components/common/promoCode';

import { getPricesConfig } from '../../getPricesConfig';
import { RootState } from '../../../store';
import { PAYMENT_TARIFF } from '../../../types';
import { getCurrencySymbol } from '../../../utils/scripts/currency';
import {
  OfferProps,
  adjustDayPrice,
  formatOptionTitle,
  priceFirstPart,
  priceSecondPart,
  useDayPrices,
  useOfferProducts,
  useSelectProduct,
} from '../offerShared';

const OfferV6 = ({ currentProduct, secondScreenWrapper }: OfferProps) => {
  const tariff = useSelector((state: RootState) => state.data.tariff);

  const { firstPriceConfig, secondPriceConfig, thirdPriceConfig } =
    getPricesConfig(tariff).everydoggyNewDesign;
  const { extraOptions } = getPricesConfig(tariff);

  const currency = getCurrencySymbol();
  const prices = useOfferProducts(firstPriceConfig, secondPriceConfig, thirdPriceConfig, tariff);
  const doOnClick = useSelectProduct();

  // On the v48 paywall the first option is "most popular" and prices show full
  // period values instead of per-week ones.
  const isMostPopularFirstOption = tariff === PAYMENT_TARIFF.V48;
  const isFullPrice = tariff === PAYMENT_TARIFF.V48;

  const firstPrice = useMemo(() => {
    const { firstOption } = prices;
    const { defaultPricesPerWeek, defaultPrices } = firstPriceConfig;

    return calculatePrice(firstOption, {
      target: isFullPrice ? 'perMonth' : 'perWeek',
      source: 'auto',
      calculateFromIntro: true,
      defaultPrice: isFullPrice ? defaultPrices : defaultPricesPerWeek,
    });
  }, [prices, firstPriceConfig]);

  const secondPrice = useMemo(() => {
    const { secondOption } = prices;
    const { defaultPricesPerWeek, defaultPrices } = secondPriceConfig;

    return calculatePrice(secondOption, {
      target: isFullPrice ? 'per3Month' : 'perWeek',
      source: 'auto',
      calculateFromIntro: true,
      defaultPrice: isFullPrice ? defaultPrices : defaultPricesPerWeek,
    });
  }, [prices, secondPriceConfig]);

  const thirdPrice = useMemo(() => {
    const { thirdOption } = prices;
    const { defaultPricesPerWeek, defaultPrices } = thirdPriceConfig;

    return calculatePrice(thirdOption, {
      target: isFullPrice ? 'per6Month' : 'perWeek',
      source: 'auto',
      calculateFromIntro: true,
      defaultPrice: isFullPrice ? defaultPrices : defaultPricesPerWeek,
    });
  }, [prices, thirdPriceConfig]);

  const { firstPriceInDay, secondPriceInDay, thirdPriceInDay } = useDayPrices(
    prices,
    firstPriceConfig,
    secondPriceConfig,
    thirdPriceConfig
  );

  const firstOldPrice = isFullPrice
    ? firstPriceConfig?.defaultOldPrices
    : firstPriceConfig?.defaultOldPricesForPeriod;
  const secondOldPrice = isFullPrice
    ? secondPriceConfig?.defaultOldPrices
    : secondPriceConfig?.defaultOldPricesForPeriod;
  const thirdOldPrice = isFullPrice
    ? thirdPriceConfig?.defaultOldPrices
    : thirdPriceConfig?.defaultOldPricesForPeriod;

  return (
    <div className={`${s.optionsWrapper} ${s.optionStyleV2}`}>
      <h2 className={s.optionsTitle}>{locales[window.userLang].tariff_title_choose}</h2>
      <PromoCode tariff={tariff} secondScreenWrapper={secondScreenWrapper} />
      <div className={s.options}>
        <Option
          className={s.option}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.firstOption?.id}
          productName={firstPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, firstPriceConfig.defaultOldPrices)}
        >
          {isMostPopularFirstOption && (
            <div className={s.bestOffer}>{extraOptions?.mostPopularTitle}</div>
          )}

          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(firstPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {firstOldPrice}
              </span>{' '}
              {currency}
              {`${firstPrice}`}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(firstPriceInDay)}.{priceSecondPart(firstPriceInDay)}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>

        <Option
          className={s.option}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.secondOption?.id}
          productName={secondPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, secondPriceConfig.defaultOldPrices)}
        >
          {!isMostPopularFirstOption && (
            <div className={s.bestOffer}>{extraOptions?.mostPopularTitle}</div>
          )}

          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(secondPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {secondOldPrice}
              </span>{' '}
              {currency}
              {secondPrice} {!isFullPrice && locales[window.userLang].payment_text_56}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(secondPriceInDay)}.{priceSecondPart(secondPriceInDay)}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>

        <Option
          className={s.option}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.thirdOption?.id}
          productName={thirdPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, thirdPriceConfig.defaultOldPrices)}
        >
          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(thirdPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {thirdOldPrice}
              </span>{' '}
              {currency}
              {thirdPrice} {!isFullPrice && locales[window.userLang].payment_text_56}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(adjustDayPrice(thirdPriceInDay))}.{priceSecondPart(adjustDayPrice(thirdPriceInDay))}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>
      </div>
    </div>
  );
};

export default OfferV6;
